import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { X, Mail, MessageCircle, Send } from "lucide-react";
import logoIconWhite from "@/assets/images/logo-icon-white.png";

const SUPPORT_EMAIL = "meajuda@moracomigo.com";
const COMMERCIAL_EMAIL = "comercial@moracomigo.com";
// TODO: trocar pelo número real do WhatsApp (só dígitos, código do país + DDD + número)
// quando o time de suporte/comercial definir o canal. Placeholder por enquanto.
const WHATSAPP_NUMBER = "5500000000000";

const WELCOME_TEXT = "Oi! Sou o assistente do Mora Comigo 👋 Como posso te ajudar hoje?";
const FALLBACK_TEXT = "Para um atendimento mais rápido, escolha uma das opções acima ou fale direto com a gente.";

function emailFlow(teamLabel, email) {
  return {
    text: `Nosso time de ${teamLabel} vai te ajudar por e-mail. Clique abaixo para enviar sua mensagem:`,
    cta: [
      { label: "Enviar e-mail", href: `mailto:${email}`, icon: Mail },
      { label: "Falar no WhatsApp", href: `https://wa.me/${WHATSAPP_NUMBER}`, icon: MessageCircle, external: true },
    ],
  };
}

const FLOWS = {
  Suporte: emailFlow("suporte", SUPPORT_EMAIL),
  Comercial: emailFlow("comercial", COMMERCIAL_EMAIL),
};

const freshThread = () => [{ role: "bot", text: WELCOME_TEXT, quickReplies: ["Suporte", "Comercial"] }];

export function ChatWidget() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [thread, setThread] = useState(freshThread);
  const [typing, setTyping] = useState(false);
  const [input, setInput] = useState("");
  const scrollRef = useRef(null);
  const panelRef = useRef(null);
  const toggleRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [thread, typing]);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e) => {
      if (panelRef.current?.contains(e.target)) return;
      if (toggleRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  // Widget de suporte simulado (sem IA) — não faz sentido dentro do chat real
  // entre usuários (/chat), onde já existe conversa de verdade na tela. Na
  // landing /listadeespera, o botão flutuante cobria o formulário no celular.
  if (location.pathname === "/chat" || location.pathname === "/listadeespera") return null;

  const botReply = (turn) => {
    setTyping(true);
    setTimeout(() => {
      setTyping(false);
      setThread((t) => [...t, { role: "bot", ...turn }]);
    }, 700);
  };

  const toggle = () => {
    setOpen((o) => {
      if (!o) setThread(freshThread()); // reabrir sempre começa do zero, sem histórico
      return !o;
    });
  };

  const pickQuickReply = (label) => {
    setThread((t) => [...t, { role: "user", text: label }]);
    botReply(FLOWS[label]);
  };

  const sendFreeText = (e) => {
    e.preventDefault();
    const value = input.trim();
    if (!value) return;
    setThread((t) => [...t, { role: "user", text: value }]);
    setInput("");
    botReply({ text: FALLBACK_TEXT, quickReplies: ["Suporte", "Comercial"] });
  };

  return (
    <>
      <button
        ref={toggleRef}
        onClick={toggle}
        aria-label={open ? "Fechar assistente" : "Abrir assistente do Mora Comigo"}
        data-testid="chat-widget-toggle"
        className="fixed bottom-5 right-5 z-[60] w-14 h-14 rounded-full mc-grad shadow-lg flex items-center justify-center hover:scale-105 transition-transform"
      >
        {open ? <X size={22} className="text-white" /> : <img src={logoIconWhite} alt="Assistente Mora Comigo" className="w-7 h-7" />}
      </button>

      {open && (
        <div ref={panelRef} data-testid="chat-widget-panel"
          className="fixed bottom-24 right-5 z-[60] w-[340px] max-w-[calc(100vw-2.5rem)] h-[460px] max-h-[70vh] mc-card overflow-hidden flex flex-col shadow-2xl mc-fade-up">
          <div className="mc-grad text-white px-4 py-3 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              <img src={logoIconWhite} alt="" className="w-6 h-6" />
              <span className="font-semibold text-sm">Assistente Mora Comigo</span>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Fechar" data-testid="chat-widget-close" className="hover:opacity-80">
              <X size={18} />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-white">
            {thread.map((turn, i) => (
              <div key={i}>
                <div className={`flex ${turn.role === "bot" ? "justify-start" : "justify-end"}`}>
                  <div className={`max-w-[85%] text-sm px-3.5 py-2.5 rounded-2xl ${
                    turn.role === "bot" ? "bg-[#f3f2f6] text-[#1e1b2e] rounded-bl-sm" : "bg-[#ece8fb] text-[#1e1b2e] rounded-br-sm"
                  }`}>
                    {turn.text}
                  </div>
                </div>
                {turn.quickReplies && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {turn.quickReplies.map((label) => (
                      <button key={label} type="button" onClick={() => pickQuickReply(label)}
                        data-testid={`chat-widget-quick-${label}`} className="mc-chip">
                        {label}
                      </button>
                    ))}
                  </div>
                )}
                {turn.cta && (
                  <div className="flex flex-col gap-2 mt-2">
                    {turn.cta.map((c) => (
                      <a key={c.label} href={c.href} {...(c.external ? { target: "_blank", rel: "noreferrer" } : {})}
                        data-testid={`chat-widget-cta-${c.label}`} className="btn-outline !justify-start text-sm">
                        <c.icon size={15} /> {c.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {typing && (
              <div className="flex justify-start">
                <div className="bg-[#f3f2f6] text-muted-foreground text-xs italic px-3.5 py-2 rounded-2xl rounded-bl-sm" data-testid="chat-widget-typing">
                  Redigindo a resposta...
                </div>
              </div>
            )}
          </div>

          <form onSubmit={sendFreeText} className="p-3 border-t border-[#f0edf7] flex items-center gap-2 shrink-0">
            <input className="mc-input py-2 text-sm !bg-[#f7f5fd]" placeholder="Continue a conversa..."
              data-testid="chat-widget-input" value={input} onChange={(e) => setInput(e.target.value)} />
            <button type="submit" className="btn-primary !px-3 !py-2" data-testid="chat-widget-send" aria-label="Enviar">
              <Send size={16} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
